import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import isPlainObject from 'lodash/isPlainObject';
import transform from 'lodash/transform';

/**
 * Is a given value empty?
 */
export const isEmpty = function (obj: unknown): boolean {
    if (obj === '' || obj === null || (obj === undefined)) {
        return true;
    }

    // Check if it's an empty array
    if (Array.isArray(obj) && obj.length === 0) {
        return true;
    }

    // Class instances (File, Blob, FormData, Date, …) carry their data on
    // non-enumerable prototype getters, so a `for…in` walk over their own
    // keys finds nothing and would wrongly report them as empty. Only treat
    // plain `{}` objects as candidates for the empty-object check.
    if (isPlainObject(obj)) {
        for (let key in obj as object) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                return false;
            }
        }

        return true;
    }

    if (isObject(obj)) {
        return false;
    }

    return false;
};

export const removeEmptyValues = <T extends object>(values: T): Partial<T> => {
    const clone = Object.assign({}, values) as Record<string, unknown>;

    Object.keys(clone).forEach((key) => {
        if (Array.isArray(clone[key])) {
            if ((clone[key] as unknown[]).length === 0) {
                delete clone[key];
            } else {
                clone[key] = (clone[key] as unknown[]).map((item) => {
                    return Array.isArray(item) || isPlainObject(item) ? removeEmptyValues(item as Record<string, unknown>) : item;
                });
            }
        } else {
            if (isEmpty(clone[key])) {
                delete clone[key];
            }
        }
    });

    return clone as Partial<T>;
};

/**
 * Find difference between two objects (skip when object is an array)
 */
export const differences = <T extends object>(origObj: T, newObj: T): Partial<T> => {
    function changes(newObj: Record<string, unknown>, origObj: Record<string, unknown>): Record<string, unknown> {
        let arrayIndexCounter = 0;

        return transform(newObj, function (result: Record<string, unknown>, value: unknown, key: string) {
            if (!isEqual(value, origObj[key])) {
                let resultKey: string | number = Array.isArray(origObj) ? arrayIndexCounter++ : key;

                result[resultKey] = value;

                // If recursion needed, the below solution works.
                //
                // if ((Array.isArray(value) && Array.isArray(origObj[key]))) {
                //     // Arrays also count as Objects....
                //     result[resultKey] = value;
                // } else if (isObject(value) && isObject(origObj[key])) {
                //     // Allow recursive support.
                //     result[resultKey] = changes(value, origObj[key]);
                // } else {
                //     result[resultKey] = value;
                // }
            }
        });
    }

    return changes(newObj as Record<string, unknown>, origObj as Record<string, unknown>) as Partial<T>;
};

export const hasOnlyDigits = (value: string): boolean => {
    return /^\d+$/.test(value);
};

/**
 * Some barcode scanners transmit a scanned code twice back-to-back
 * (e.g. "8435593901601" becomes "84355939016018435593901601"). When the
 * value is an exact head-to-tail duplicate of itself, collapse it back
 * down to the single code.
 */
export const normalizeScannedCode = (value: string): string => {
    const half = value.length / 2;

    if (value.length > 0 && value.length % 2 === 0 && value.slice(0, half) === value.slice(half)) {
        return value.slice(0, half);
    }

    return value;
};

export const getEmptyValues = (values: object): Record<string, unknown> => {
    const definition: Record<string, unknown> = {};
    const source = values as Record<string, unknown>;

    Object.keys(source).forEach((key) => {
        if (typeof source[key] === 'object') {
            definition[key] = [];
        } else if (typeof source[key] === 'boolean') {
            definition[key] = false;
        } else {
            definition[key] = '';
        }
    });

    return definition;
};

export const removeFalseProperties = <T extends object>(object: T): Partial<T> => {
    const source = object as Record<string, unknown>;

    return Object.fromEntries(Object.entries(object).filter(([
        key,
    ]) => {
        return source[key];
    })) as Partial<T>;
};

export const wrap = (data: object, keys: string[] = []): Record<string, unknown> => {
    const values = Object.assign({}, data) as Record<string, unknown>;

    keys.forEach((key) => {
        if ((values[key] === undefined) || (values[key] === null)) {
            return;
        }

        if (Array.isArray(values[key])) {
            return;
        }

        values[key] = [
            values[key],
        ];
    });

    return values;
};

export const unwrap = (data: object, keys: string[] = []): Record<string, unknown> => {
    const values = Object.assign({}, data) as Record<string, unknown>;

    keys.forEach((key) => {
        if ((values[key] === undefined) || (values[key] === null)) {
            return;
        }

        if (Array.isArray(values[key])) {
            values[key] = (values[key] as unknown[])[0];
        }
    });

    return values;
};

export const overwrite = <T extends object>(original: T, ...various: Array<object | null | undefined>): T => {
    const data = Object.assign({}, original) as Record<string, unknown>;
    const originalKeys = Object.keys(original);

    various.forEach((toCopy) => {
        if (!toCopy) {
            // Allowed `null` values.
            return;
        }

        const source = toCopy as Record<string, unknown>;
        const copyableKeys = Object.keys(source);

        originalKeys.forEach((key) => {
            if (~copyableKeys.indexOf(key)) {
                if ((source[key] === null)) {
                    data[key] = '';
                } else {
                    data[key] = source[key];
                }
            }
        });
    });

    return data as T;
};
